import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Legend } from "recharts";
import { TrendingUp } from "lucide-react";
import { Card, Eyebrow, Kpi, monthLabel } from "./ui";
import GoalsPanel from "./GoalsPanel";
import { TrainingVideoButton } from "./TrainingVideo";

const axis = { fontSize: 10, fill: "#475569", fontFamily: "IBM Plex Mono" };
const tip = { background: "#fff", border: "1px solid #0F172A", borderRadius: 0, fontSize: 12 };

export default function ClientAdvisory() {
  const [d, setD] = useState(null);
  const [params] = useSearchParams();
  useEffect(() => {
    api.get("/advisory/client-view").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);
  if (!d) return null;
  const b = d.burn;
  const f = d.forecast;
  const rec = d.debt_plan.plans?.[d.debt_plan.recommended];
  const hist = b.months.slice(-6).map((m) => ({ month: m.month, actual: m.ending_cash, gross_burn: m.gross_burn, inflow: m.inflow }));
  if (hist.length) hist[hist.length - 1].forecast = hist[hist.length - 1].actual;
  const chart = [...hist, ...f.months.map((m) => ({ month: m.month, forecast: m.ending_cash }))];
  return (
    <section data-testid="client-advisory-section">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={16} className="text-[var(--ll-ink-muted)]" />
        <div className="font-display font-bold text-lg">Your financial outlook</div>
        <div className="text-xs text-[var(--ll-ink-soft)] ml-auto">Read-only · prepared by your accountant</div>
        <TrainingVideoButton video="client" label="Watch: your outlook in 60 seconds" className="!py-1.5 !text-xs" testid="client-outlook-video-btn" autoOpen={params.get("video") === "outlook"} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Kpi testid="client-adv-cash" label="Cash on hand" value={fmtMoney(b.cash_now)} tone={b.cash_now < 0 ? "bad" : undefined} />
        <Kpi testid="client-adv-burn" label="Avg net burn / mo" value={fmtMoney(b.avg_net_burn["3"])} tone={b.avg_net_burn["3"] > 0 ? "bad" : "good"} sub="Last 3 months" />
        <Kpi testid="client-adv-runway" label="Runway" tone={b.cash_flow_positive ? "good" : undefined}
          value={b.cash_flow_positive ? "Positive" : b.cash_now < 0 ? "Negative" : `${b.runway_months} mo`}
          sub={b.cash_flow_positive ? "Money in covers money out" : b.cash_now < 0 ? "Cash is below $0" : b.runway_until ? `Until ~${monthLabel(b.runway_until)}` : ""} />
        <Kpi testid="client-adv-credit" label="Cash/credit needed (12 mo)" value={fmtMoney(f.credit_needed)} tone={f.credit_needed > 0 ? "bad" : "good"}
          sub={f.first_shortfall_month ? `Below safety buffer by ${monthLabel(f.first_shortfall_month)}` : "Stays above safety buffer"} />
      </div>
      <Card className="mb-3" testid="client-adv-chart-card">
        <Eyebrow>Cash: last 6 months & 12-month outlook</Eyebrow>
        <div className="h-60 mt-3" data-testid="client-adv-chart">
          <ResponsiveContainer>
            <ComposedChart data={chart} margin={{ right: 28, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" tick={axis} tickFormatter={monthLabel} />
              <YAxis tick={axis} />
              <Tooltip contentStyle={tip} formatter={(v) => fmtMoney(v)} labelFormatter={monthLabel} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={d.cash_buffer} stroke="#D97706" strokeDasharray="4 4" label={{ value: "Safety buffer", fontSize: 10, fill: "#B45309" }} />
              <Bar dataKey="inflow" name="Money in" fill="#059669" />
              <Bar dataKey="gross_burn" name="Money out" fill="#DC2626" />
              <Line dataKey="actual" name="Cash" stroke="#0F172A" strokeWidth={2} dot={false} />
              <Line dataKey="forecast" name="Forecast" stroke="#0F172A" strokeDasharray="5 4" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card testid="client-adv-tax">
          <Eyebrow>Taxes (estimate)</Eyebrow>
          <div className="mt-2 text-sm space-y-1 font-mono-fin">
            <div className="flex justify-between"><span>Estimated yearly tax</span><b>{fmtMoney(d.tax.estimated.total)}</b></div>
            <div className="flex justify-between"><span>Quarterly payment</span><b data-testid="client-adv-quarterly">{fmtMoney(d.tax.quarterly_estimate)}</b></div>
            <div className="flex justify-between text-[var(--ll-success)]"><span>Possible savings found</span><b>{fmtMoney(d.tax.strategy_savings_total)}</b></div>
          </div>
          <ul className="mt-3 text-xs space-y-1.5">
            {d.tax.strategies.map((s) => (
              <li key={s.key} className="flex justify-between gap-3" data-testid={`client-adv-strategy-${s.key}`}><span>{s.title}</span><span className="font-mono-fin text-[var(--ll-success)]">~{fmtMoney(s.est_savings)}</span></li>
            ))}
          </ul>
          <div className="text-[10px] text-[var(--ll-ink-muted)] mt-3">Estimates using {d.tax.rates_year} US federal rates — discuss with your accountant before acting.</div>
        </Card>
        <Card testid="client-adv-debt">
          <Eyebrow>Debt payoff plan</Eyebrow>
          {d.debts.length === 0 ? <div className="text-sm mt-2 text-[var(--ll-ink-soft)]">No debts on file.</div> : (
            <>
              <div className="mt-2 text-sm font-mono-fin flex justify-between"><span>Total debt</span><b>{fmtMoney(d.debt_plan.total_balance)}</b></div>
              {rec && (
                <div className="mt-1 text-sm" data-testid="client-adv-debt-free">
                  Debt-free by <b>{monthLabel(rec.debt_free_month)}</b> paying {fmtMoney(d.debt_plan.extra_payment)}/mo extra
                  {rec.interest_saved != null && <> — saves <b className="text-[var(--ll-success)]">{fmtMoney(rec.interest_saved)}</b> in interest</>}
                </div>
              )}
              <ol className="mt-3 text-xs space-y-1">
                {(rec?.order || []).map((o, i) => (
                  <li key={`${o.name}-${i}`} className="flex justify-between"><span>{i + 1}. {o.name}</span><span className="font-mono-fin">paid off {monthLabel(o.payoff_date)}</span></li>
                ))}
              </ol>
            </>
          )}
        </Card>
      </div>
      <GoalsPanel testid="client-goals-panel" />
    </section>
  );
}
